import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton.jsx';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import clsx from 'clsx';

const STAGES = ['lead', 'prospect', 'negotiating', 'qualified', 'quoted', 'won', 'lost'];
const TOUCH_ICON = { visit: '🚗', call: '📞', email: '✉️', quote: '💲', note: '✏️' };

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const q = useQuery({
    queryKey: ['opportunity', id],
    queryFn: () => api.get(`/opportunities/${id}`).then(r => r.data.data),
  });

  const update = useMutation({
    mutationFn: (body) => api.put(`/opportunities/${id}`, body).then(r => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opportunity', id] });
      qc.invalidateQueries({ queryKey: ['opportunities'] });
      setEditing(false);
    },
  });

  if (q.isLoading) return <div className="text-muted">Loading…</div>;
  if (q.isError) return <div className="text-crimson">Failed to load deal — {q.error?.response?.data?.error || 'unknown error'}</div>;

  const d = q.data;
  const isOpen = !['won', 'lost'].includes(d.stage);

  return (
    <div className="space-y-4 max-w-2xl">
      <header>
        <div className="section-eyebrow">
          <Link to="/pipeline" className="hover:underline">Pipeline</Link> · Deal
        </div>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h1 className="text-2xl">{d.name}</h1>
          <div className="flex gap-2">
            {!editing && <button onClick={() => setEditing(true)} className="btn-ghost text-sm">Edit</button>}
            <BackButton fallback="/pipeline" />
          </div>
        </div>
        <div className="rule-crimson" />
      </header>

      {editing ? (
        <EditForm deal={d} onSave={(body) => update.mutate(body)} onCancel={() => setEditing(false)} saving={update.isPending} error={update.error?.response?.data?.error} />
      ) : (
        <section className="card p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Stage">
              <span className={clsx('capitalize font-medium', d.stage === 'won' ? 'text-emerald-700' : d.stage === 'lost' ? 'text-crimson' : 'text-navy')}>
                {d.stage}
              </span>
            </Field>
            <Field label="Value">{d.value_usd != null ? `$${Number(d.value_usd).toLocaleString()}` : '—'}</Field>
            <Field label="Cost">{d.cost_usd != null ? `$${Number(d.cost_usd).toLocaleString()}` : '—'}</Field>
            <Field label="Probability">{d.probability != null ? `${d.probability}%` : '—'}</Field>
            <Field label="Expected close">{d.expected_close_date ? new Date(d.expected_close_date).toLocaleDateString() : '—'}</Field>
            <Field label="Customer">
              {d.customer_id
                ? <Link to={`/customers/${d.customer_id}`} className="text-crimson hover:underline">{d.customer_name}</Link>
                : d.source_customer_name
                  ? <span>{d.source_customer_name} <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">unlinked</span></span>
                  : '—'}
            </Field>
            <Field label="Rep">{d.user_name || '—'}</Field>
            {d.domain && <Field label="Domain"><span className="capitalize">{d.domain}</span></Field>}
            {d.lead_source && <Field label="Lead source"><span className="capitalize">{d.lead_source.replace(/_/g, ' ')}</span></Field>}
            <Field label="Opened">{new Date(d.created_at).toLocaleDateString()}</Field>
            {d.source_note_date && new Date(d.source_note_date).toDateString() !== new Date(d.created_at).toDateString() && (
              <Field label="Activity">{new Date(d.source_note_date).toLocaleDateString()}</Field>
            )}
            {!isOpen && d.closed_at && <Field label="Closed">{new Date(d.closed_at).toLocaleDateString()}</Field>}
          </div>
          {d.description && (
            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Description</div>
              <p className="text-navy whitespace-pre-wrap">{d.description}</p>
            </div>
          )}
          {d.lost_reason && (
            <div className="text-sm">
              <div className="text-[10px] uppercase tracking-wider text-muted mb-1">Lost reason</div>
              <p className="text-crimson">{d.lost_reason}</p>
            </div>
          )}
        </section>
      )}

      {d.recent_notes?.length > 0 && (
        <section className="card p-4">
          <div className="section-eyebrow">Recent activity — {d.customer_name}</div>
          <div className="rule-crimson" />
          <div className="space-y-1 mt-2">
            {d.recent_notes.map(n => (
              <Link key={n.id} to={`/notes/${n.id}`}
                className="block text-xs py-1.5 px-2 rounded hover:bg-panel flex items-baseline gap-2">
                <span>{TOUCH_ICON[n.touch_type] || '✏️'}</span>
                <span className="flex-1 truncate text-navy">{n.summary || '(note)'}</span>
                <span className="text-muted whitespace-nowrap">{new Date(n.created_at).toLocaleDateString()}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted mb-0.5">{label}</div>
      <div className="text-navy">{children}</div>
    </div>
  );
}

function EditForm({ deal, onSave, onCancel, saving, error }) {
  const [form, setForm] = useState({
    name: deal.name || '',
    stage: deal.stage || 'prospect',
    value_usd: deal.value_usd ?? '',
    cost_usd: deal.cost_usd ?? '',
    probability: deal.probability ?? '',
    expected_close_date: deal.expected_close_date ? deal.expected_close_date.slice(0, 10) : '',
    description: deal.description || '',
    lost_reason: deal.lost_reason || '',
  });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <section className="card p-4 space-y-3">
      {error && <div className="text-crimson text-sm">{error}</div>}
      <div>
        <label className="label">Deal name</label>
        <input className="input" value={form.name} onChange={set('name')} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Stage</label>
          <select className="input" value={form.stage} onChange={set('stage')}>
            {STAGES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Value ($)</label>
          <input className="input" type="number" min="0" value={form.value_usd} onChange={set('value_usd')} />
        </div>
        <div>
          <label className="label">Cost ($)</label>
          <input className="input" type="number" min="0" value={form.cost_usd} onChange={set('cost_usd')} />
        </div>
        <div>
          <label className="label">Probability (%)</label>
          <input className="input" type="number" min="0" max="100" value={form.probability} onChange={set('probability')} />
        </div>
        <div>
          <label className="label">Expected close</label>
          <input className="input" type="date" value={form.expected_close_date} onChange={set('expected_close_date')} />
        </div>
      </div>
      <div>
        <label className="label">Description</label>
        <textarea className="input min-h-[80px]" value={form.description} onChange={set('description')} />
      </div>
      {form.stage === 'lost' && (
        <div>
          <label className="label">Lost reason</label>
          <input className="input" value={form.lost_reason} onChange={set('lost_reason')} />
        </div>
      )}
      <div className="flex gap-2 justify-end">
        <button className="btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn-primary" disabled={saving || !form.name.trim()} onClick={() => onSave(form)}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}
